import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ControlContainer, NgForm } from '@angular/forms';
import { DialogService } from '@progress/kendo-angular-dialog';
import * as _ from 'lodash';
import { BehaviorSubject, debounceTime, Observable, Subscription, switchMap } from 'rxjs';
import { AddressSearch, FormattedAddress, SearchAddress } from 'src/app/models/address-search.model';
import { FormWizardPageElement } from 'src/app/models/form-wizard-template.model';
import { AddressSearchService } from 'src/app/services/address-search.service';
import { EditAddressDialogComponent } from '../../dialog/edit-address-dialog/edit-address-dialog.component';

@Component({
  selector: 'app-address-input-element',
  templateUrl: './address-input-element.component.html',
  styleUrls: ['./address-input-element.component.scss'],
  viewProviders: [ { provide: ControlContainer, useExisting: NgForm } ]
})
export class AddressInputElementComponent implements OnInit, OnDestroy {
  @Input() element: FormWizardPageElement | undefined;
  @Input() formValues: any | undefined;

  private searchSubject = new BehaviorSubject<string>('');
  private searchSubscription: Subscription;
  public addressesSubject = new BehaviorSubject<Array<SearchAddress>>([]);
  public searchInProgress: boolean = false;
  public loadingAddress: boolean = false;

  public get localValue(): string {
    return this.getAddressPreview();
  }

  public set localValue(value: string) {
  }

  constructor(
    private addressSearchService: AddressSearchService,
    private dialogService: DialogService) {
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(500),
      switchMap((value) => this.seachAddresses(value))).subscribe({
        next: (result) => {
          this.addressesSubject.next(result.addresses);
          this.searchInProgress = false;
        },
        error: (error) => {
          this.searchInProgress = false;
        }
      });
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this.searchSubject.unsubscribe();
    this.searchSubscription.unsubscribe();
    this.addressesSubject.unsubscribe();
  }

  public handleFilter(search: string): void {
    this.searchSubject.next(search);

    // Note that we also have to set touched state of the hidden textbox
    const element = document.getElementById(this.getControlName()) as HTMLElement;
    if (!_.isNil(element) && !this.isTouched()) {
      element.classList.add('ng-touched');
    }
  }

  public onAddressChange(value: SearchAddress): void {
    if (!_.isNil(value)) {
      if (value.populated) {
        this.setValue(value.formattedAddress);
      } else {
        this.populateAddress(value);
      }
    }
  }

  public getControlName(): string {
    return 'input' + this.getName();
  }

  public getName(): string {
    return this.element?.name ?? '';
  }

  public getLabel(): string {
    return this.element?.label ?? '';
  }

  public isLabelVisible(): boolean {
    return this.getLabel() !== '' && (this.isControlVisible() || this.isAutocompleteControlVisible());
  }

  public isControlVisible(): boolean {
    return !_.isNil(this.element) && !_.isNil(this.formValues);
  }

  public isAutocompleteControlVisible(): boolean {
    return !_.isNil(this.element) && !_.isNil(this.formValues) && !this.isValuePopulated();
  }

  public isDeleteVisible(): boolean {
    return this.isControlVisible() && !_.isNil(this.formValues[this.getName()]);
  }

  public isRequired(): boolean {
    return this.element?.required ?? false;
  }

  public isInputDisabled(): boolean {
    return this.loadingAddress;
  }

  public isSearchDisabled(): boolean {
    return this.loadingAddress;
  }

  public isInvalid(): boolean {
    return this.isRequired() && this.getAddressPreview() === '';
  }

  public isTouched(): boolean {
    const element = document.getElementById(this.getControlName()) as HTMLElement;

    return element?.classList?.contains('ng-touched');
  }

  public onEditClick(): void {
    const addressDialog = this.dialogService.open({
      content: EditAddressDialogComponent
    });
    addressDialog.content.instance.defaultCountryId = this.element?.defaultSearchCountry;

    const value = this.formValues[this.getName()];

    if (!_.isNil(value)) {
      const searchAddress = new SearchAddress();
      searchAddress.formattedAddress.line1 = value?.line1;
      searchAddress.formattedAddress.line2 = value?.line2;
      searchAddress.formattedAddress.line3 = value?.line3;
      searchAddress.formattedAddress.suburb = value?.suburb;
      searchAddress.formattedAddress.postcode = value?.postcode;
      searchAddress.formattedAddress.state = value?.state;
      searchAddress.formattedAddress.country = value?.country;

      addressDialog.content.instance.value = searchAddress;
    }

    addressDialog.result.subscribe((result: any) => {
      if (!_.isNil(result?.formattedAddress)) {
        this.setValue(result.formattedAddress);
      }
    });
  }

  public onDeleteClick(): void {
    this.formValues[this.getName()] = null;
  }

  public getAddressPreview(): string {
    let result = '';

    const value: FormattedAddress = this.formValues[this.getName()];

    if (!_.isNil(value)) {
      result = value.getAddressLine();
    }

    return result;
  }

  private seachAddresses(address: string): Observable<AddressSearch> {
    this.searchInProgress = true;

    return this.addressSearchService.getAddressSearch(address, this.element?.defaultSearchCountry);
  }

  private populateAddress(value: SearchAddress): void {
    this.loadingAddress = true;

    this.addressSearchService.postAddressPopulate(value.id ?? '').subscribe({
      next: (result) => this.populateAddressOk(result),
      error: (error) => this.populateAddressError(error.error)
    })
  }

  private populateAddressOk(result: SearchAddress): void {
    this.setValue(result?.formattedAddress);
    this.loadingAddress = false;
  }

  private populateAddressError(error: any): void {
    this.loadingAddress = false;
  }

  private isValuePopulated(): boolean {
    return this.getAddressPreview() !== '';
  }

  private setValue(value: FormattedAddress)
  {
    const newValue = new FormattedAddress();

    if (!_.isNil(value)) {
      newValue.line1 = value.line1 ?? '';
      newValue.line2 = value.line2 ?? '';
      newValue.line3 = value.line3 ?? '';
      newValue.suburb = value.suburb ?? '';
      newValue.postcode = value.postcode ?? '';
      newValue.state = value.state ?? '';
      newValue.country = value.country ?? '';
      newValue.dpid = value.dpid ?? '';
    }

    this.formValues[this.getName()] = newValue;
  }
}
